<template>
	<div
		v-if="items?.length"
		class="rewards-grid"
		:class="`rewards-grid--count-${count} rewards-grid--${context}`"
	>
		<div v-for="(item, idx) in items" :key="idx" class="rewards-grid__item">
			<Tooltip
				v-if="item"
				:heading="
					isMultiplier(item.key)
						? multiplierHeading(
								translate(`resource.${item.key}`),
								item.qty,
							)
						: translate(`resource.${item.key}`)
				"
				customWidth="18rem"
				:placement="Placements.TOP"
			>
				<template #element>
					<div class="rewards-grid__value">
						<div
							class="rewards-grid__icon"
							:class="`offer-${contentKind}-icon--${item.key.toLowerCase()}-${dynamicSize.icon}`"
						/>
					</div>
					<div class="rewards-grid__label">
						<div
							v-if="isMultiplier(item.key)"
							class="computed-value"
							:style="{
								fontSize: `${dynamicSize.price / 16}rem`,
							}"
						>
							<span>
								{{
									`${formatNumber(multiplierMinValue(item.qty))} min`
								}}
							</span>
						</div>
						<AppPrice
							v-else
							:amount="item.qty"
							:currency="item.key"
							:fontSize="dynamicSize.price"
							:iconSize="dynamicSize.priceIcon"
						/>
					</div>
				</template>
				<template #tooltip>
					<span class="reward-tooltip">
						{{
							replacePlaceholder(
								translate(`resource.${item.key}_description`),
								"{number}",
								getReplaceValue(item.key) ?? 0,
							)
						}}
					</span>
				</template>
			</Tooltip>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { ResourceOrUtilitySlotItem } from "@/types/shop";
import { CommodityGroup } from "@/types/shop";
import { Placements, RewardsGridContext } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import {
	getReplaceValue,
	isMultiplier,
	multiplierHeading,
	multiplierMinValue,
} from "@/utils/shop/values";
import { translate } from "@/utils/translate";
import { computed } from "vue";

interface Props {
	items: ResourceOrUtilitySlotItem[] | null;
	context: RewardsGridContext;
}

const { items, context } = defineProps<Props>();

const count = computed((): number => items?.length ?? 0);

const contentKind = computed(
	(): CommodityGroup.RESOURCE | CommodityGroup.UTILITY | null => {
		if (!items) return null;
		const first = items[0];
		if (!first) return null;
		if (first.group === CommodityGroup.RESOURCE)
			return CommodityGroup.RESOURCE;
		if (first.group === CommodityGroup.UTILITY)
			return CommodityGroup.UTILITY;
		return null;
	},
);

const dynamicSize = computed((): Record<string, number> => {
	if (context === RewardsGridContext.CARD) {
		return { icon: 80, price: 12, priceIcon: 14 };
	}
	return { icon: 80, price: 20, priceIcon: 10 };
});
</script>

<style lang="scss" scoped>
.rewards-grid {
	@include flex-row-center;
	position: relative;
	gap: 0.125rem; // SLIM fix
	z-index: 1;
	padding: 0.625rem 0; // SLIM fix

	.price {
		:deep(.shrink-text) {
			color: #fff3dd;
		}
	}

	&__item {
		@include flex-col-center;
		row-gap: 6px;
		justify-items: center;
		width: 100%;

		.v-popper {
			width: 100%;
		}

		:deep(.computed-value) {
			@include flex-row-center;
			gap: 0.25rem;
			z-index: 1;
			margin: 0 0.2rem;
			color: #fff3dd;
			font-weight: 800;

			span {
				padding-bottom: 0.25rem;
			}
		}
	}

	&__value {
		position: relative;
		width: 100%;
		min-width: 4rem;
		height: 3.75rem; // SLIM fix

		.rewards-grid__icon {
			position: absolute; // to prevent the sprite from overflowing the grid with transparent areas
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
		}
	}

	&__label {
		@include flex-row-center;
		position: relative;
		mask-image: linear-gradient(
			to right,
			transparent 2%,
			black 20%,
			black 80%,
			transparent 98%
		);
		border-width: 0.063rem 0 0.063rem 0;
		border-style: solid;
		border-image-source: linear-gradient(
			to bottom,
			rgba(216, 169, 114, 1),
			rgba(226, 181, 145, 1)
		);
		border-image-slice: 1;
		width: 100%;

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			@include background(
				url($path-textures + "silk-flag-texture-bg.avif"),
				cover,
				center center,
				no-repeat
			);
			z-index: 1;
			mix-blend-mode: multiply;
			width: 100%;
			height: 100%;
			content: "";
		}

		&::after {
			position: absolute;
			top: 0;
			left: 0;
			background: rgb(112, 62, 16);
			width: 100%;
			height: 100%;
			content: "";
		}

		.price {
			z-index: 1;
			margin: 0 1.375rem; // SLIM fix
		}
	}
}
</style>
